/**
 * Workflow Template Service
 * Handles template definitions, loading, and workflow generation from templates
 */

import { WorkflowTemplate } from '../components/templates/WorkflowTemplateModal';
import type { Workflow } from '../types/workflow';
import type { WorkflowNode } from '../types/node-definitions';

export interface TemplateWorkflowData {
  nodes: Record<string, WorkflowNode>;
  connections: any[];
  metadata: {
    templateId: string;
    templateName: string;
    createdFrom: string;
    version: string;
  };
}

export class WorkflowTemplateService {
  
  /**
   * Generate workflow data from template
   */
  static generateWorkflowFromTemplate(template: WorkflowTemplate): TemplateWorkflowData {
    switch (template.id) {
      case 'empty':
        return this.generateEmptyTemplate(template);
      case 'chat-assistant':
        return this.generateChatAssistantTemplate(template);
      case 'image-generator':
        return this.generateImageGeneratorTemplate(template);
      case 'data-processor':
        return this.generateDataProcessorTemplate(template);
      case 'content-creator':
        return this.generateContentCreatorTemplate(template);
      case 'api-integration':
        return this.generateApiIntegrationTemplate(template);
      default:
        return this.generateEmptyTemplate(template);
    }
  }

  /**
   * Empty Canvas Template
   */
  private static generateEmptyTemplate(template: WorkflowTemplate): TemplateWorkflowData {
    return {
      nodes: {},
      connections: [],
      metadata: {
        templateId: template.id,
        templateName: template.name,
        createdFrom: 'template',
        version: '1.0.0'
      }
    };
  }

  /**
   * Chat Assistant Template
   * Pre-configured chat workflow with AI integration and memory
   */
  private static generateChatAssistantTemplate(template: WorkflowTemplate): TemplateWorkflowData {
    const nodes: Record<string, WorkflowNode> = {
      'input_message': {
        id: 'input_message',
        definitionId: 'input-text',
        fieldValues: {
          position: { x: 100, y: 100 },
          label: 'User Message',
          placeholder: 'Enter your message...',
          multiline: true
        }
      },
      'chat_memory': {
        id: 'chat_memory',
        definitionId: 'memory-context',
        fieldValues: {
          position: { x: 100, y: 250 },
          memoryType: 'conversation',
          maxMessages: 10
        }
      },
      'system_prompt': {
        id: 'system_prompt',
        definitionId: 'prompt-template',
        fieldValues: {
          position: { x: 300, y: 100 },
          template: 'You are a helpful AI assistant. Respond to the user\'s message in a friendly and informative way.',
          templateType: 'system'
        }
      },
      'ai_response': {
        id: 'ai_response',
        definitionId: 'llm-chat',
        fieldValues: {
          position: { x: 500, y: 200 },
          provider: 'openai',
          model: 'gpt-4',
          temperature: 0.7,
          maxTokens: 1000
        }
      },
      'format_response': {
        id: 'format_response',
        definitionId: 'text-formatter',
        fieldValues: {
          position: { x: 700, y: 200 },
          format: 'markdown',
          addTimestamp: true
        }
      },
      'output_response': {
        id: 'output_response',
        definitionId: 'output-text',
        fieldValues: {
          position: { x: 900, y: 200 },
          label: 'AI Response'
        }
      },
      'save_to_memory': {
        id: 'save_to_memory',
        definitionId: 'memory-save',
        fieldValues: {
          position: { x: 700, y: 350 },
          saveUserMessage: true,
          saveAssistantResponse: true
        }
      },
      'trigger_node': {
        id: 'trigger_node',
        definitionId: 'trigger-manual',
        fieldValues: {
          position: { x: 100, y: 50 },
          triggerName: 'Send Message'
        }
      }
    };

    const connections = [
      { id: 'conn_1', from: { nodeId: 'trigger_node', outputId: 'trigger' }, to: { nodeId: 'input_message', inputId: 'trigger' } },
      { id: 'conn_2', from: { nodeId: 'input_message', outputId: 'text' }, to: { nodeId: 'ai_response', inputId: 'userMessage' } },
      { id: 'conn_3', from: { nodeId: 'system_prompt', outputId: 'prompt' }, to: { nodeId: 'ai_response', inputId: 'systemPrompt' } },
      { id: 'conn_4', from: { nodeId: 'chat_memory', outputId: 'context' }, to: { nodeId: 'ai_response', inputId: 'context' } },
      { id: 'conn_5', from: { nodeId: 'ai_response', outputId: 'response' }, to: { nodeId: 'format_response', inputId: 'text' } },
      { id: 'conn_6', from: { nodeId: 'format_response', outputId: 'formatted' }, to: { nodeId: 'output_response', inputId: 'text' } },
      { id: 'conn_7', from: { nodeId: 'input_message', outputId: 'text' }, to: { nodeId: 'save_to_memory', inputId: 'userMessage' } },
      { id: 'conn_8', from: { nodeId: 'ai_response', outputId: 'response' }, to: { nodeId: 'save_to_memory', inputId: 'assistantResponse' } },
      { id: 'conn_9', from: { nodeId: 'save_to_memory', outputId: 'saved' }, to: { nodeId: 'chat_memory', inputId: 'update' } }
    ];

    return {
      nodes,
      connections,
      metadata: {
        templateId: template.id,
        templateName: template.name,
        createdFrom: 'template',
        version: '1.0.0'
      }
    };
  }

  /**
   * Image Generator Template
   * Complete image generation setup with prompt engineering and style controls
   */
  private static generateImageGeneratorTemplate(template: WorkflowTemplate): TemplateWorkflowData {
    const nodes: Record<string, WorkflowNode> = {
      'prompt_input': {
        id: 'prompt_input',
        definitionId: 'input-text',
        fieldValues: {
          position: { x: 100, y: 100 },
          label: 'Image Prompt',
          placeholder: 'Describe the image you want to generate...',
          multiline: true
        }
      },
      'style_selector': {
        id: 'style_selector',
        definitionId: 'input-select',
        fieldValues: {
          position: { x: 100, y: 250 },
          label: 'Art Style',
          options: ['Realistic', 'Cartoon', 'Anime', 'Oil Painting', 'Digital Art', 'Photography'],
          defaultValue: 'Realistic'
        }
      },
      'prompt_enhancer': {
        id: 'prompt_enhancer',
        definitionId: 'llm-prompt-enhance',
        fieldValues: {
          position: { x: 350, y: 150 },
          enhancementType: 'image_generation',
          addQualityTags: true,
          addStyleTags: true
        }
      },
      'negative_prompt': {
        id: 'negative_prompt',
        definitionId: 'input-text',
        fieldValues: {
          position: { x: 100, y: 400 },
          label: 'Negative Prompt',
          placeholder: 'What to avoid in the image...',
          defaultValue: 'blurry, low quality, distorted, ugly, bad anatomy'
        }
      },
      'image_settings': {
        id: 'image_settings',
        definitionId: 'image-generation-settings',
        fieldValues: {
          position: { x: 350, y: 350 },
          width: 512,
          height: 512,
          steps: 30,
          cfgScale: 7.5,
          seed: -1
        }
      },
      'image_generator': {
        id: 'image_generator',
        definitionId: 'stable-diffusion-generate',
        fieldValues: {
          position: { x: 600, y: 200 },
          model: 'stable-diffusion-xl',
          sampler: 'DPM++ 2M Karras'
        }
      },
      'image_output': {
        id: 'image_output',
        definitionId: 'output-image',
        fieldValues: {
          position: { x: 850, y: 200 },
          label: 'Generated Image',
          showMetadata: true
        }
      },
      'save_image': {
        id: 'save_image',
        definitionId: 'file-save',
        fieldValues: {
          position: { x: 850, y: 350 },
          format: 'png',
          path: '/generated-images/',
          autoNaming: true
        }
      },
      'batch_controller': {
        id: 'batch_controller',
        definitionId: 'batch-control',
        fieldValues: {
          position: { x: 350, y: 50 },
          batchSize: 4,
          enableBatch: false
        }
      },
      'trigger_generate': {
        id: 'trigger_generate',
        definitionId: 'trigger-manual',
        fieldValues: {
          position: { x: 100, y: 50 },
          triggerName: 'Generate Image'
        }
      }
    };

    const connections = [
      { id: 'conn_1', from: { nodeId: 'trigger_generate', outputId: 'trigger' }, to: { nodeId: 'batch_controller', inputId: 'trigger' } },
      { id: 'conn_2', from: { nodeId: 'prompt_input', outputId: 'text' }, to: { nodeId: 'prompt_enhancer', inputId: 'prompt' } },
      { id: 'conn_3', from: { nodeId: 'style_selector', outputId: 'selected' }, to: { nodeId: 'prompt_enhancer', inputId: 'style' } },
      { id: 'conn_4', from: { nodeId: 'prompt_enhancer', outputId: 'enhanced' }, to: { nodeId: 'image_generator', inputId: 'prompt' } },
      { id: 'conn_5', from: { nodeId: 'negative_prompt', outputId: 'text' }, to: { nodeId: 'image_generator', inputId: 'negativePrompt' } },
      { id: 'conn_6', from: { nodeId: 'image_settings', outputId: 'settings' }, to: { nodeId: 'image_generator', inputId: 'settings' } },
      { id: 'conn_7', from: { nodeId: 'batch_controller', outputId: 'batch' }, to: { nodeId: 'image_generator', inputId: 'batchSettings' } },
      { id: 'conn_8', from: { nodeId: 'image_generator', outputId: 'image' }, to: { nodeId: 'image_output', inputId: 'image' } },
      { id: 'conn_9', from: { nodeId: 'image_generator', outputId: 'image' }, to: { nodeId: 'save_image', inputId: 'data' } }
    ];

    return {
      nodes,
      connections,
      metadata: {
        templateId: template.id,
        templateName: template.name,
        createdFrom: 'template',
        version: '1.0.0'
      }
    };
  }

  /**
   * Data Processing Template
   * Basic data processing pipeline
   */
  private static generateDataProcessorTemplate(template: WorkflowTemplate): TemplateWorkflowData {
    const nodes: Record<string, WorkflowNode> = {
      'data_input': {
        id: 'data_input',
        definitionId: 'input-file',
        fieldValues: {
          position: { x: 100, y: 150 },
          label: 'Data File',
          acceptedFormats: ['csv', 'json', 'xlsx']
        }
      },
      'data_validator': {
        id: 'data_validator',
        definitionId: 'data-validator',
        fieldValues: {
          position: { x: 300, y: 150 },
          validationRules: ['required_fields', 'data_types', 'range_check']
        }
      },
      'data_transformer': {
        id: 'data_transformer',
        definitionId: 'data-transform',
        fieldValues: {
          position: { x: 500, y: 150 },
          transformations: ['clean_whitespace', 'normalize_dates', 'remove_duplicates']
        }
      },
      'data_output': {
        id: 'data_output',
        definitionId: 'output-file',
        fieldValues: {
          position: { x: 700, y: 150 },
          format: 'csv',
          filename: 'processed_data.csv'
        }
      }
    };

    const connections = [
      { id: 'conn_1', from: { nodeId: 'data_input', outputId: 'file' }, to: { nodeId: 'data_validator', inputId: 'data' } },
      { id: 'conn_2', from: { nodeId: 'data_validator', outputId: 'validated' }, to: { nodeId: 'data_transformer', inputId: 'data' } },
      { id: 'conn_3', from: { nodeId: 'data_transformer', outputId: 'transformed' }, to: { nodeId: 'data_output', inputId: 'data' } }
    ];

    return {
      nodes,
      connections,
      metadata: {
        templateId: template.id,
        templateName: template.name,
        createdFrom: 'template',
        version: '1.0.0'
      }
    };
  }

  /**
   * Content Creator Template
   * End-to-end content creation workflow
   */
  private static generateContentCreatorTemplate(template: WorkflowTemplate): TemplateWorkflowData {
    const nodes: Record<string, WorkflowNode> = {
      'topic_input': {
        id: 'topic_input',
        definitionId: 'input-text',
        fieldValues: {
          position: { x: 100, y: 100 },
          label: 'Content Topic',
          placeholder: 'What topic should we write about?'
        }
      },
      'research_ai': {
        id: 'research_ai',
        definitionId: 'llm-research',
        fieldValues: {
          position: { x: 300, y: 100 },
          provider: 'openai',
          model: 'gpt-4',
          researchDepth: 'comprehensive'
        }
      },
      'content_writer': {
        id: 'content_writer',
        definitionId: 'llm-writer',
        fieldValues: {
          position: { x: 500, y: 100 },
          contentType: 'blog_post',
          tone: 'professional',
          length: 'medium'
        }
      },
      'seo_optimizer': {
        id: 'seo_optimizer',
        definitionId: 'seo-optimizer',
        fieldValues: {
          position: { x: 700, y: 100 },
          targetKeywords: [],
          optimizeHeadings: true,
          addMetaDescription: true
        }
      },
      'content_output': {
        id: 'content_output',
        definitionId: 'output-text',
        fieldValues: {
          position: { x: 900, y: 100 },
          label: 'Final Content',
          format: 'markdown'
        }
      }
    };

    const connections = [
      { id: 'conn_1', from: { nodeId: 'topic_input', outputId: 'text' }, to: { nodeId: 'research_ai', inputId: 'topic' } },
      { id: 'conn_2', from: { nodeId: 'research_ai', outputId: 'research' }, to: { nodeId: 'content_writer', inputId: 'research' } },
      { id: 'conn_3', from: { nodeId: 'content_writer', outputId: 'content' }, to: { nodeId: 'seo_optimizer', inputId: 'content' } },
      { id: 'conn_4', from: { nodeId: 'seo_optimizer', outputId: 'optimized' }, to: { nodeId: 'content_output', inputId: 'text' } }
    ];

    return {
      nodes,
      connections,
      metadata: {
        templateId: template.id,
        templateName: template.name,
        createdFrom: 'template',
        version: '1.0.0'
      }
    };
  }

  /**
   * API Integration Template
   * Multi-API orchestration with error handling
   */
  private static generateApiIntegrationTemplate(template: WorkflowTemplate): TemplateWorkflowData {
    const nodes: Record<string, WorkflowNode> = {
      'api_input': {
        id: 'api_input',
        definitionId: 'input-json',
        fieldValues: {
          position: { x: 100, y: 150 },
          label: 'API Request Data',
          schema: {}
        }
      },
      'api_call_1': {
        id: 'api_call_1',
        definitionId: 'http-request',
        fieldValues: {
          position: { x: 300, y: 100 },
          method: 'GET',
          url: 'https://api.example.com/data',
          headers: {}
        }
      },
      'api_call_2': {
        id: 'api_call_2',
        definitionId: 'http-request',
        fieldValues: {
          position: { x: 300, y: 200 },
          method: 'POST',
          url: 'https://api2.example.com/process',
          headers: {}
        }
      },
      'data_mapper': {
        id: 'data_mapper',
        definitionId: 'data-mapper',
        fieldValues: {
          position: { x: 500, y: 150 },
          mappingRules: {}
        }
      },
      'error_handler': {
        id: 'error_handler',
        definitionId: 'error-handler',
        fieldValues: {
          position: { x: 500, y: 300 },
          retryAttempts: 3,
          fallbackResponse: {}
        }
      },
      'response_output': {
        id: 'response_output',
        definitionId: 'output-json',
        fieldValues: {
          position: { x: 700, y: 150 },
          label: 'API Response'
        }
      }
    };

    const connections = [
      { id: 'conn_1', from: { nodeId: 'api_input', outputId: 'data' }, to: { nodeId: 'api_call_1', inputId: 'body' } },
      { id: 'conn_2', from: { nodeId: 'api_call_1', outputId: 'response' }, to: { nodeId: 'api_call_2', inputId: 'body' } },
      { id: 'conn_3', from: { nodeId: 'api_call_2', outputId: 'response' }, to: { nodeId: 'data_mapper', inputId: 'data' } },
      { id: 'conn_4', from: { nodeId: 'data_mapper', outputId: 'mapped' }, to: { nodeId: 'response_output', inputId: 'data' } },
      { id: 'conn_5', from: { nodeId: 'api_call_1', outputId: 'error' }, to: { nodeId: 'error_handler', inputId: 'error' } },
      { id: 'conn_6', from: { nodeId: 'api_call_2', outputId: 'error' }, to: { nodeId: 'error_handler', inputId: 'error' } }
    ];

    return {
      nodes,
      connections,
      metadata: {
        templateId: template.id,
        templateName: template.name,
        createdFrom: 'template',
        version: '1.0.0'
      }
    };
  }

  /**
   * Get template by ID
   */
  static getTemplateById(templateId: string): WorkflowTemplate | null {
    // This would typically fetch from the WorkflowTemplateModal templates array
    // For now, we return null as templates are defined in the modal component
    return null;
  }

  /**
   * Save workflow generated from template
   */
  static saveWorkflowFromTemplate(templateData: TemplateWorkflowData, workflowName: string) {
    const workflowId = `workflow_${Date.now()}`;
    
    // Create workflow structure compatible with existing storage
    const workflowData = {
      id: workflowId,
      name: workflowName,
      description: `Created from ${templateData.metadata.templateName} template`,
      nodes: templateData.nodes,
      connections: templateData.connections,
      isFunction: false,
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString(),
      metadata: templateData.metadata
    };

    return workflowData;
  }
}